import { RefObject, useEffect } from 'react';
import dayjs from 'dayjs';
import fallback from '../../../../_Translations/generated/date-range-input.en.t.json';
import Keys from '../../../../_Translations/generated/date-range-input.en.t.json.keys';
import useTranslation from '../../../Hooks/useTranslation';
import localeData from 'dayjs/plugin/localeData';
import { parseInt } from 'lodash-es';
import { OnChangeDate } from './DateWithKeyboardSupport';

dayjs.extend(localeData);

const getNewDate = (hoveredDay: string, month: number, year: number): Date => {
  return dayjs(
    `${year}-${(month + 1).toString().length === 1 ? '0' : ''}${month + 1}-${hoveredDay}`
  ).toDate();
};

export const getMonthIndex = (month: Element): number => {
  return dayjs
    .months()
    .findIndex(
      currentMonth =>
        currentMonth ===
        (month.querySelector(
          '.react-datepicker__current-month'
        ) as HTMLDivElement)?.innerText.split(' ')[0]
    );
};

export const getYear = (month: Element): number => {
  return parseInt(
    (month.querySelector('.react-datepicker__current-month') as HTMLDivElement)?.innerText.split(
      ' '
    )[1],
    10
  );
};

const isLeftSideSelectedMonth = (wrapper: HTMLDivElement, month: number): boolean => {
  const months = wrapper.querySelectorAll('.react-datepicker__month-container');
  return !(!!months[1] && getMonthIndex(months[1]) === month);
};

const getFilteredDays = (
  month: number,
  year: number,
  filterFn: (currentDate: Date) => boolean,
  wrapper: HTMLDivElement,
  elements?: NodeListOf<Element>
): Element[] => {
  return Array.from(
    elements ||
      wrapper.querySelectorAll('.react-datepicker__day--excluded, .react-datepicker__day--disabled')
  ).filter(element => {
    const monthContainer = element.closest('.react-datepicker__month-container');
    const currentDate = getNewDate(
      element.innerHTML,
      isLeftSideSelectedMonth(wrapper, month)
        ? !!monthContainer?.nextSibling
          ? month
          : month + 1
        : !!monthContainer?.nextSibling
        ? month - 1
        : month,
      year
    );
    return filterFn(currentDate);
  });
};

const getAllDays = (
  wrapper: HTMLDivElement,
  callback: (day: HTMLDivElement, monthIndex: number) => void
) => {
  const monthContainers = wrapper.querySelectorAll('.react-datepicker__month-container');
  monthContainers.forEach((container: Element, idx: number) => {
    const datePickerDays = container.querySelectorAll('.react-datepicker__day');
    (datePickerDays as NodeListOf<HTMLDivElement>).forEach((day: HTMLDivElement) =>
      callback(day, idx)
    );
  });
};

const getSelectedDate = (date: Date | [Date | null, Date | null] | null): Date => {
  const extractedDate = Array.isArray(date) && !!date[0] ? date[0] : (date as Date);
  extractedDate?.setHours(0, 0, 0, 0);
  return extractedDate;
};

const useAdvancedFeatures = (
  isCalendarOpen: boolean,
  currentlyOpenMonth: number,
  currentlyOpenYear: number,
  date: OnChangeDate,
  isDateSelected: boolean,
  datePickerWrapperRef: RefObject<HTMLDivElement | null>,
  excludeDates?: Date[] | { date: Date; message?: string | undefined }[]
) => {
  const { t } = useTranslation('date-range-input', fallback, Keys);

  useEffect(() => {
    if (!datePickerWrapperRef.current) {
      return;
    }
    const hoverEvent = (e: MouseEvent): void => {
      getAllDays(datePickerWrapperRef.current as HTMLDivElement, (day: HTMLDivElement) => {
        setTimeout(() => {
          if (day.classList.contains('react-datepicker__day--excluded')) {
            day.classList.remove('react-datepicker__day--excluded');
            if (!day.classList.contains('react-datepicker__day--disabled')) {
              day.classList.add('react-datepicker__day--disabled');
            }
          }
        }, 300);
      });
      const target = e.target as HTMLDivElement;
      const monthContainer = target.closest('.react-datepicker__month-container')!;
      const hoveredDay = target.innerText;

      const hoveredDate = getNewDate(
        hoveredDay,
        getMonthIndex(monthContainer),
        getYear(monthContainer)
      );

      if (e.type === 'mouseenter') {
        if (
          !!getSelectedDate(date) &&
          isDateSelected &&
          !!hoveredDate &&
          dayjs(hoveredDate).isAfter(dayjs(getSelectedDate(date)), 'day') &&
          Array.isArray(date) &&
          !date[1]
        ) {
          const nights = dayjs(hoveredDate).diff(getSelectedDate(date), 'day');

          let minimumStayMarkup = '';
          if (
            target.classList.contains('react-datepicker__day--excluded') ||
            target.classList.contains('react-datepicker__day--disabled')
          ) {
            const filteredElements = getFilteredDays(
              currentlyOpenMonth,
              currentlyOpenYear,
              (currentDate: Date) =>
                dayjs(currentDate).isAfter(dayjs(getSelectedDate(date)), 'day') &&
                dayjs(currentDate).isBefore(dayjs(hoveredDate), 'day'),
              datePickerWrapperRef.current as HTMLDivElement
            );
            if (
              (filteredElements.length > 0 && filteredElements.length === nights - 1) ||
              dayjs(hoveredDate)
                .subtract(1, 'day')
                .isSame(dayjs(getSelectedDate(date)), 'day')
            ) {
              filteredElements.forEach(element => {
                element.classList.add('react-datepicker__day--within-invalid-range');
              });
              target.classList.add('react-datepicker__day--within-invalid-range');
              minimumStayMarkup =
                !!excludeDates && excludeDates.length > 0
                  ? t(Keys.minimumStay, {
                      count: excludeDates.length + 1
                    })
                  : '';
              getAllDays(datePickerWrapperRef.current as HTMLDivElement, (day: HTMLDivElement) => {
                day.classList.remove('react-datepicker__day--in-selecting-range');
              });
            }
          } else {
            const filteredElements = getFilteredDays(
              currentlyOpenMonth,
              currentlyOpenYear,
              (currentDate: Date) =>
                dayjs(currentDate).isAfter(dayjs(getSelectedDate(date)), 'day') &&
                dayjs(currentDate).isBefore(dayjs(hoveredDate), 'day'),
              datePickerWrapperRef.current as HTMLDivElement,
              (datePickerWrapperRef.current as HTMLDivElement).querySelectorAll(
                '.react-datepicker__day'
              )
            );
            filteredElements.forEach(element => {
              element.classList.add('react-datepicker__day--in-selecting-range');
              element.classList.remove('react-datepicker__day--within-invalid-range');
            });
            target.classList.add('react-datepicker__day--in-selecting-range');
          }

          target.insertAdjacentHTML(
            'beforeend',
            `<span class="react-datepicker__day__popup${
              !!minimumStayMarkup ? ' react-datepicker__day__popup--large' : ''
            } m-0"><span class="d-block">${nights} ${t(Keys.advancedNight, {
              count: nights
            })}</span class="m-0">${minimumStayMarkup}</span>`
          );
        }
      } else if (e.type === 'mouseleave') {
        if (!!target.querySelector('.react-datepicker__day__popup')) {
          target.querySelector('.react-datepicker__day__popup')?.remove();
        }
        const filteredElements = getFilteredDays(
          currentlyOpenMonth,
          currentlyOpenYear,
          (currentDate: Date) =>
            dayjs(currentDate).isAfter(dayjs(getSelectedDate(date)).add(1, 'day')) &&
            dayjs(currentDate).isBefore(dayjs(hoveredDate)),
          datePickerWrapperRef.current as HTMLDivElement
        );
        if (filteredElements.length > 0) {
          filteredElements.forEach(element => {
            if (element.classList.contains('react-datepicker__day--within-invalid-range')) {
              element.classList.remove('react-datepicker__day--within-invalid-range');
            }
          });
          target.classList.remove('react-datepicker__day--within-invalid-range');
        }
        getAllDays(datePickerWrapperRef.current as HTMLDivElement, (day: HTMLDivElement) => {
          if (day.classList.contains('react-datepicker__day--in-selecting-range')) {
            day.classList.remove('react-datepicker__day--in-selecting-range');
          }
        });
      }
    };

    if (isCalendarOpen) {
      setTimeout(() => {
        getAllDays(datePickerWrapperRef.current as HTMLDivElement, (day: HTMLDivElement) => {
          day.removeEventListener('mouseenter', (evt: MouseEvent) => hoverEvent(evt));
          day.addEventListener('mouseenter', (evt: MouseEvent) => hoverEvent(evt));
          day.removeEventListener('mouseleave', (evt: MouseEvent) => hoverEvent(evt));
          day.addEventListener('mouseleave', (evt: MouseEvent) => hoverEvent(evt));
        });
      }, 300);

      return () => {
        getAllDays(datePickerWrapperRef.current as HTMLDivElement, (day: HTMLDivElement) => {
          day.removeEventListener('mouseenter', (evt: MouseEvent) => hoverEvent(evt));
          day.removeEventListener('mouseleave', (evt: MouseEvent) => hoverEvent(evt));
        });
      };
    }
  }, [isCalendarOpen, currentlyOpenMonth, currentlyOpenYear, date, datePickerWrapperRef]);
};

export default useAdvancedFeatures;
